import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "HOME", id: "home" },
  { to: "/services", label: "SERVICES", id: "services" },
  { to: "/experience", label: "EXPERIENCE", id: "experience" },
  { to: "/about", label: "ABOUT", id: "about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
        scrolled ? "glass-panel border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-baseline gap-2 group">
          <span className="font-unbounded font-semibold tracking-tight text-lg text-white group-hover:text-cyan transition-colors duration-300">
            IQ·AI
          </span>
          <span className="font-mono text-[10px] tracking-[0.3em] text-neutral-500 group-hover:text-neutral-300 transition-colors duration-300">
            SOLUTIONS
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `relative font-mono text-[11px] tracking-[0.25em] transition-colors duration-300 py-2 ${
                  isActive ? "text-cyan" : "text-neutral-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-0 -bottom-0.5 h-px bg-cyan transition-all duration-500 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-talk"
            className="group flex items-center gap-2 border border-cyan/50 px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] text-cyan hover:bg-cyan hover:text-black transition-colors duration-300"
          >
            LET'S TALK
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-white p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden glass-panel border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className={({ isActive }) =>
                    `font-mono text-xs tracking-[0.3em] ${isActive ? "text-cyan" : "text-neutral-300"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-cta-talk"
                className="mt-2 inline-flex w-fit items-center gap-2 border border-cyan/50 px-5 py-2.5 font-mono text-[11px] tracking-[0.25em] text-cyan"
              >
                LET'S TALK <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
